import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock, Download, MessageCircle, ArrowRight, CheckCircle2,
  AlertTriangle, Loader2, ShieldCheck, PiggyBank, TrendingUp, Target, Umbrella, Sparkles, MailCheck, Calculator,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../lib/api";
import { track } from "../lib/analytics";
import { ScoreMeter } from "../components/ScoreMeter";
import { SiteFooter, BrandLogo } from "../components/SiteChrome";
import { useBrand } from "../context/BrandContext";
import { useLang } from "../context/LanguageContext";
import { colorFor, AGE_GROUPS, INCOME_RANGES, MARITAL, DEPENDENTS, CHILDREN, EXISTING_INVESTMENTS, MONTHLY_INVESTMENT, PRIMARY_GOALS } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BookingDialog } from "../components/BookingDialog";

const CAT_ICONS = { PROTECT: ShieldCheck, PREPARE: Umbrella, GROW: TrendingUp, GOALS: Target, RETIRE: PiggyBank, KNOW: Sparkles };
const CALC_FOR = { RETIRE: "retirement", GOALS: "goal", GROW: "sip", PREPARE: "networth", PROTECT: "sip", KNOW: "sip" };
const CALC_LABEL = { retirement: "Retirement Calculator", goal: "Goal Planning Calculator", sip: "SIP Calculator", networth: "Net Worth Calculator" };

export default function Result() {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const s = useBrand();
  const { t, lang } = useLang();

  const [report, setReport] = useState(location.state?.report || null);
  const [loading, setLoading] = useState(!location.state?.report);
  const [leadId, setLeadId] = useState(null);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    track("report_viewed", { assessment_id: assessmentId });
    if (!report) {
      api.get(`/assessments/${assessmentId}`)
        .then((res) => { setReport(res.data.report); })
        .catch(() => toast.error("Could not load your report."))
        .finally(() => setLoading(false));
    }
  }, [assessmentId]);

  useEffect(() => { if (report) track("lead_form_viewed"); }, [report]);

  if (loading || !report) {
    return <div className="min-h-screen grid place-items-center text-navy"><Loader2 className="animate-spin" /></div>;
  }

  const cats = Object.values(report.categories);
  const applicable = cats.filter((c) => c.applicable);
  const ratingColor = colorFor(report.rating.color);

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")}><BrandLogo /></button>
          <div className="text-xs tracking-[0.18em] uppercase text-slate-400 font-semibold">{t("your_report")}</div>
        </div>
      </div>

      {/* Score hero */}
      <section className="brand-gradient relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-5xl mx-auto px-5 py-14 grid md:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="text-xs tracking-[0.2em] uppercase text-gold font-bold">{t("report_overline")}</div>
            <h1 className="mt-3 font-heading font-extrabold text-white text-3xl md:text-4xl tracking-tight">
              {t("report_title")}
            </h1>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-heading font-bold"
              style={{ background: ratingColor, color: "#fff" }} data-testid="rating-label">
              {(t("rating_labels")[report.rating.label]) || report.rating.label}
            </div>
            <p className="mt-5 text-white/70 text-sm max-w-md leading-relaxed">
              {t("report_sub")}
            </p>
          </motion.div>
          <div className="grid place-items-center">
            <div className="rounded-3xl bg-white/95 p-8 card-shadow">
              <ScoreMeter score={report.score} colorKey={report.rating.color} label={report.rating.band} />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-14">
        {/* strongest / attention */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
            <div className="flex items-center gap-2 text-brandgreen font-heading font-bold"><CheckCircle2 size={18} /> {t("strongest")}</div>
            <div className="mt-4 space-y-2">
              {report.strongest_areas.map((a) => (
                <div key={a.category} className="flex justify-between text-sm">
                  <span className="text-navy font-semibold">{a.label}</span>
                  <span className="text-slate-500">{a.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
            <div className="flex items-center gap-2 text-branddanger font-heading font-bold"><AlertTriangle size={18} /> {t("attention")}</div>
            <div className="mt-4 space-y-2">
              {report.attention_areas.map((a) => (
                <div key={a.category} className="flex justify-between text-sm">
                  <span className="text-navy font-semibold">{a.label}</span>
                  <span className="text-slate-500">{a.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* category cards */}
        <h2 className="mt-14 font-heading font-extrabold text-navy text-2xl tracking-tight">{t("category_breakdown")}</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cats.map((c) => {
            const Icon = CAT_ICONS[c.code] || Sparkles;
            const col = colorFor(c.color);
            return (
              <div key={c.code} className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow" data-testid={`category-card-${c.code}`}>
                <div className="flex items-center justify-between">
                  <div className="grid place-items-center h-10 w-10 rounded-xl" style={{ background: `${col}1a` }}>
                    <Icon size={18} style={{ color: col }} />
                  </div>
                  <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full"
                    style={{ background: `${col}1a`, color: col }}>{c.status}</span>
                </div>
                <div className="mt-4 font-heading font-bold text-navy">{c.label}</div>
                {c.applicable ? (
                  <>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="font-heading font-extrabold text-2xl text-navy">{c.percentage}%</span>
                      <span className="text-xs text-slate-400">({c.score}/{c.max})</span>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${c.percentage}%`, background: col }} />
                    </div>
                  </>
                ) : (
                  <div className="mt-2 text-sm text-slate-400">Not applicable to you</div>
                )}
                <p className="mt-3 text-xs text-slate-500 leading-relaxed">{c.description}</p>
              </div>
            );
          })}
        </div>

        {/* Lead gate OR full report */}
        {!unlocked ? (
          <LeadGate assessmentId={assessmentId} report={report}
            onSuccess={(id) => { setLeadId(id); setUnlocked(true); }} />
        ) : (
          <FullReport report={report} leadId={leadId} settings={s} />
        )}
      </div>

      <SiteFooter />
    </div>
  );
}

// ---------------- Lead Gate ----------------
function LeadGate({ assessmentId, report, onSuccess }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", city: "", age_group: "", income_range: "",
    consent: false, marital_status: "", dependents: "", children: "",
    existing_investments: [], current_monthly_investment: "", primary_goal: "",
  });
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInv = (name) =>
    setForm((f) => ({
      ...f,
      existing_investments: f.existing_investments.includes(name)
        ? f.existing_investments.filter((x) => x !== name)
        : [...f.existing_investments, name],
    }));

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Please enter your name.");
    if (!/^[0-9+\-\s]{7,15}$/.test(form.mobile.trim())) return toast.error("Please enter a valid mobile number.");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Please enter a valid email.");
    if (!form.consent) return toast.error("Please provide consent to continue.");
    setSubmitting(true);
    try {
      const payload = { assessment_id: assessmentId, ...form,
        existing_investments: form.existing_investments,
        age_group: form.age_group || null, income_range: form.income_range || null,
        marital_status: form.marital_status || null, dependents: form.dependents || null,
        children: form.children || null, current_monthly_investment: form.current_monthly_investment || null,
        primary_goal: form.primary_goal || null, city: form.city || null };
      const res = await api.post("/leads", payload);
      track("lead_submitted", { priority: res.data.priority });
      toast.success("Your full report is ready!");
      onSuccess(res.data.lead_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed.");
      setSubmitting(false);
    }
  };

  const Sel = ({ label, k, options, testid }) => (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Select value={form[k]} onValueChange={(v) => set(k, v)}>
        <SelectTrigger data-testid={testid} className="mt-1.5 rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="mt-14 relative">
      <div className="rounded-3xl border border-gold/30 bg-white card-shadow overflow-hidden">
        <div className="brand-gradient px-8 py-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-30" />
          <div className="relative">
            <Lock className="mx-auto text-gold" />
            <h3 className="mt-3 font-heading font-extrabold text-white text-2xl tracking-tight">Your Financial Health Report Is Ready</h3>
            <p className="mt-2 text-white/70 text-sm">Where should we send your detailed report and Top 3 priorities?</p>
          </div>
        </div>

        <div className="p-8">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600">Full Name *</Label>
              <Input data-testid="lead-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5 rounded-lg" placeholder="Your name" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Mobile Number *</Label>
              <Input data-testid="lead-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1.5 rounded-lg" placeholder="10-digit mobile" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Email Address *</Label>
              <Input data-testid="lead-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-lg" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">City</Label>
              <Input data-testid="lead-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 rounded-lg" placeholder="City" />
            </div>
            <Sel label="Age Group" k="age_group" options={AGE_GROUPS} testid="lead-age" />
            <div className="sm:col-span-2">
              <Sel label="Annual Income Range" k="income_range" options={INCOME_RANGES} testid="lead-income" />
            </div>
          </div>

          <button onClick={() => setShowMore((v) => !v)} className="mt-5 text-sm text-royal font-semibold hover:underline" data-testid="toggle-more">
            {showMore ? "Hide optional questions" : "Help us personalise further (optional)"}
          </button>

          {showMore && (
            <div className="mt-4 grid sm:grid-cols-2 gap-5 border-t border-slate-100 pt-5">
              <Sel label="Marital Status" k="marital_status" options={MARITAL} testid="q-marital" />
              <Sel label="Dependents" k="dependents" options={DEPENDENTS} testid="q-dependents" />
              <Sel label="Children" k="children" options={CHILDREN} testid="q-children" />
              <Sel label="Current Monthly Investment" k="current_monthly_investment" options={MONTHLY_INVESTMENT} testid="q-monthly" />
              <div className="sm:col-span-2">
                <Sel label="Primary Financial Goal" k="primary_goal" options={PRIMARY_GOALS.map((g) => ({ value: g, label: g }))} testid="q-goal" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-600">Existing Investments</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {EXISTING_INVESTMENTS.map((inv) => {
                    const on = form.existing_investments.includes(inv);
                    return (
                      <button key={inv} type="button" data-testid={`inv-${inv}`} onClick={() => toggleInv(inv)}
                        className="rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-200"
                        style={{ borderColor: on ? "#123D73" : "#E2E8F0", background: on ? "rgba(18,61,115,0.08)" : "#fff", color: on ? "#123D73" : "#64748B" }}>
                        {inv}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <label className="mt-6 flex items-start gap-3 cursor-pointer">
            <Checkbox data-testid="lead-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
            <span className="text-xs text-slate-500 leading-relaxed">
              I agree to be contacted regarding my financial health assessment and financial planning discussion.
            </span>
          </label>

          <button data-testid="lead-submit" onClick={submit} disabled={submitting}
            className="mt-6 w-full rounded-full bg-gold text-navy font-heading font-bold py-4 flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-300 disabled:opacity-60">
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <>GET MY FINANCIAL HEALTH REPORT <ArrowRight size={18} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Full Report ----------------
function FullReport({ report, leadId, settings }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const priorityColors = ["#D64545", "#F2A900", "#123D73"];
  const download = () => {
    track("pdf_downloaded", { lead_id: leadId });
    window.open(`${API_BASE}/leads/${leadId}/pdf`, "_blank");
  };
  const whatsapp = () => {
    track("whatsapp_clicked");
    const num = (settings.whatsapp_number || "").replace(/\D/g, "");
    const msg = encodeURIComponent(`Hi, I just completed my Financial Health Check-Up (Score ${report.score}/60) and would like to discuss my improvement plan.`);
    if (!num) return toast.info("WhatsApp number not configured yet.");
    window.open(`https://wa.me/${num}?text=${msg}`, "_blank");
  };

  return (
    <div className="mt-14" data-testid="full-report">
      <h2 className="font-heading font-extrabold text-navy text-2xl tracking-tight">{t("top3_title")}</h2>
      <div className="mt-6 grid md:grid-cols-3 gap-5">
        {report.priorities.map((p, i) => (
          <div key={p.category} className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow flex flex-col" data-testid={`priority-${i + 1}`}>
            <div className="font-heading font-extrabold text-3xl" style={{ color: priorityColors[i] }}>0{i + 1}</div>
            <div className="mt-3 font-heading font-bold text-navy">{t("priority_titles")[p.category] || p.title}</div>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{t("priority_messages")[p.category] || p.message}</p>
            <button data-testid={`priority-calc-${i + 1}`}
              onClick={() => { track("calculators_opened", { from: "priority", category: p.category }); navigate(`/calculators?tab=${CALC_FOR[p.category] || "sip"}`); }}
              className="mt-4 self-start text-sm font-semibold text-royal flex items-center gap-1.5 hover:gap-2.5 transition-all duration-200">
              <Calculator size={15} /> {CALC_LABEL[CALC_FOR[p.category] || "sip"]} →
            </button>
          </div>
        ))}
      </div>

      {report.gaps.length > 0 && (
        <>
          <h2 className="mt-14 font-heading font-extrabold text-navy text-2xl tracking-tight">{t("gaps_title")}</h2>
          <div className="mt-6 space-y-4">
            {report.gaps.map((g) => (
              <div key={g.code} className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow flex gap-4">
                <div className="mt-1 h-2.5 w-2.5 rounded-full shrink-0" style={{ background: g.severity === "high" ? "#D64545" : "#F2A900" }} />
                <div>
                  <div className="font-heading font-bold text-navy">{t("gap_titles")[g.code] || g.title}</div>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">{t("gap_suggestions")[g.code] || g.suggestion}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-6 rounded-2xl bg-royal/5 border border-royal/15 p-6 text-sm text-slate-600 leading-relaxed">
        <strong className="text-navy">{t("please_note")}</strong> {settings.disclaimer}
      </div>

      {/* CTA */}
      <div className="mt-14 rounded-3xl brand-gradient relative overflow-hidden p-10 text-center card-shadow">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative">
          <h3 className="font-heading font-extrabold text-white text-2xl md:text-3xl tracking-tight">{t("ready_title")}</h3>
          <p className="mt-3 text-white/70 max-w-2xl mx-auto text-sm">
            {t("ready_sub")}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button data-testid="cta-whatsapp" onClick={whatsapp} className="rounded-full bg-brandgreen text-white font-heading font-semibold px-6 py-3 flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-300">
              <MessageCircle size={18} /> {t("whatsapp_us")}
            </button>
            <BookingDialog leadId={leadId} triggerLabel={t("book_meeting")}
              triggerClassName="rounded-full border border-white/25 text-white font-heading font-semibold px-6 py-3 flex items-center gap-2 hover:bg-white/10 transition-colors duration-300" />
            <button data-testid="cta-download" onClick={download} className="rounded-full bg-gold text-navy font-heading font-bold px-6 py-3 flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-300">
              <Download size={18} /> {t("download_report")}
            </button>
          </div>
          <button data-testid="cta-calculators" onClick={() => { track("calculators_opened"); navigate("/calculators"); }}
            className="mt-4 text-white/70 text-sm underline underline-offset-4 hover:text-white transition-colors duration-200">
            {t("try_calculators")}
          </button>
          <div className="mt-5 flex items-center justify-center gap-2 text-white/50 text-xs">
            <MailCheck size={14} /> {t("emailed_note")}
          </div>
        </div>
      </div>
    </div>
  );
}
